import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import { CONFIG } from "../config";

export default function FailuresPreventedGraph({ selectedDate, selectedStatus, selectedAgeRange }) {
  const navigate = useNavigate();
  const containerRef = useRef();
  const svgRef = useRef();
  const tooltipRef = useRef();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const restrictedIds = JSON.parse(process.env.REACT_APP_RESTRICTED_USER_ID || '[]');
  const userId = parseInt(localStorage.getItem("tensor-user-id"), 10);
  const isRestricted = restrictedIds.includes(userId);

  // Resize observer for responsiveness
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { width } = entry.contentRect;
      setDimensions({ width, height: 75 }); // fixed height
    });

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Fetch data from API
  useEffect(() => {
    if (!selectedDate) return; // wait until selectedDate is available

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${CONFIG.API_BASE_URL}/${CONFIG.FAILURES_PREVENTED_URL}/${localStorage.getItem("tensor-user-id")}?date_str=${selectedDate}&selected_status=${selectedStatus}&selected_agerange=${selectedAgeRange}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("tensor-auth-token"),
            },
          }
        );

        if (response.status === 401) {
          localStorage.removeItem('tensor-auth-token');
          localStorage.removeItem('tensor-user-id');
          navigate('/', { state: { message: 'Session expired please try again.', type: 'error' } });
          return;
        }

        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Failed to fetch failures prevented data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, selectedStatus, selectedAgeRange]);

  useEffect(() => {
    if (dimensions.width === 0 || data.length === 0) return;

    const parseDate = d3.timeParse("%Y-%m-%d");
    const formatDate = d3.timeFormat("%m/%d");

    const dataset = data.map(d => ({
      ...d,
      date: parseDate(d.date)
    }));

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const { width, height } = dimensions;
    const margin = { top: 5, right: 15, bottom: 20, left: 15 };

    const xScale = d3.scaleTime()
      .domain(d3.extent(dataset, d => d.date))
      .range([margin.left, width - margin.right]);

    const maxValue = d3.max(dataset, d => d.value);
    const yScale = d3.scaleLinear()
      .domain([d3.min(dataset, d => d.value) - 5, maxValue + 2])
      .range([height - margin.bottom, margin.top]);

    const xAxis = d3.axisBottom(xScale)
      .ticks(d3.timeWeek.every(1))
      .tickFormat(formatDate);

    const line = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.value))
      .curve(d3.curveMonotoneX);

    const area = d3.area()
      .x(d => xScale(d.date))
      .y0(height - margin.bottom)
      .y1(d => yScale(d.value))
      .curve(d3.curveMonotoneX);

    const defs = svg.append("defs");
    const gradient = defs.append("linearGradient")
      .attr("id", "line-gradient")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#032FCE")
      .attr("stop-opacity", 0.4);

    gradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#032FCE")
      .attr("stop-opacity", 0);

    svg.append("path")
      .datum(dataset)
      .attr("fill", "url(#line-gradient)")
      .attr("d", area);

    svg.append("path")
      .datum(dataset)
      .attr("fill", "none")
      .attr("stroke", "#032FCE")
      .attr("stroke-width", 2)
      .attr("d", line);

    const last = dataset[27];
    const lastX = xScale(last.date);
    const lastY = yScale(last.value);

    svg.append("circle")
      .attr("cx", lastX)
      .attr("cy", lastY)
      .attr("r", 4)
      .attr("fill", "#032FCE");

    svg.append("text")
      .attr("x", lastX - 30)
      .attr("y", 9)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("fill", "#032FCE")
      .text(`${formatDate(last.date)}: ${last.value}`);

    const tooltip = d3.select(tooltipRef.current);

    svg.selectAll("rect.hover-zone")
      .data(dataset)
      .join("rect")
      .attr("class", "hover-zone")
      .attr("x", d => xScale(d.date) - 5)
      .attr("y", margin.top)
      .attr("width", 10)
      .attr("height", height - margin.top - margin.bottom)
      .attr("fill", "transparent")
      .on("mouseover", (event, d) => {
        tooltip
          .style("opacity", 1)
          .style("z-index", `10000`)
          .style("left", `${xScale(d.date) + 30}px`)
          .style("top", `${yScale(d.value) + 20}px`)
          .html(`<div class="bg-white shadow px-2 py-1 rounded text-xs">${formatDate(d.date)}: ${d.value} </div>`);
      })
      .on("mouseout", () => {
        tooltip.style("opacity", 0);
      });

    svg.append("g")
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .call(xAxis)
      .selectAll("text")
      .attr("class", "text-xs text-gray-400");

  }, [dimensions, data]);

  return (
    <div ref={containerRef} className="w-full px-4 pt-2 rounded shadow bg-white relative">
      {loading ? (
        <div className="flex justify-center items-center min-h-[180px]">
          <svg className="w-6 h-6 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
        </div>
      ) : (
        <div className="min-h-[135px]">
          <p className="text-sm text-gray-500 block_head">Failures Avoided</p>
          {isRestricted && selectedStatus !== "at_risk"  && selectedStatus !== null ? (
            <div className="flex justify-center items-center min-h-[100px]">
              <p className="text-gray-400 text-sm">No Data Available</p>
            </div>
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-600">
                {isRestricted
                  ? data.length > 7
                    ? data[27].value.toLocaleString()
                    : "-"
                  : data.length > 0
                  ? data.reduce((acc, curr) => acc + curr.value, 0).toLocaleString()
                  : "-"}
              </p>
              <svg ref={svgRef} width={dimensions.width} height={dimensions.height}></svg>
              <div
                ref={tooltipRef}
                className="absolute pointer-events-none transition-opacity duration-200"
                style={{ position: 'absolute', opacity: 0 }}
              ></div>
            </>
          )}
        </div>
      )}
    </div>
  );
}